// Utility helpers for batching and concurrency

/**
 * Splits an array into chunks of a given size.
 */
export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [];
  const numberOfChunks = Math.ceil(array.length / chunkSize);
  return Array.from({ length: numberOfChunks }, (_, index) =>
    array.slice(index * chunkSize, (index + 1) * chunkSize)
  );
};

/**
 * Runs an array of async tasks with a concurrency limit.
 */
export async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onProgress?: (completed: number, total: number) => void
): Promise<T[]> {
  const results: T[] = [];
  let completed = 0;
  let nextIndex = 0;
  const total = tasks.length;

  async function worker(): Promise<void> {
    const current = nextIndex;
    nextIndex += 1;
    if (current >= tasks.length) return;
    const result = await tasks[current]();
    results[current] = result;
    completed += 1;
    if (onProgress) onProgress(completed, total);
    await worker();
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () =>
    worker()
  );
  await Promise.all(workers);
  return results;
}
